import { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Sparkle, FileText, Plus, PencilSimple, Trash, Gear, CaretDown } from "@phosphor-icons/react";

/** Composer toolbar: AI draft (with optional steer) + saved replies picker. `onInsert(text)` fills the body. */
export function ComposerAssist({ item, onInsert, hasText }) {
  const [drafting, setDrafting] = useState(false);
  const [steer, setSteer] = useState("");
  const [showSteer, setShowSteer] = useState(false);
  const [tpl, setTpl] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  useEffect(() => {
    if (!pickerOpen || tpl) return;
    API.get("/inbox/templates", { params: { item_id: item.id } }).then(({ data }) => setTpl(data)).catch(() => setTpl({ templates: [], can_manage: false }));
  }, [pickerOpen, tpl, item.id]);

  const [confirmOpen, setConfirmOpen] = useState(false);

  const draft = async () => {
    if (hasText) { setConfirmOpen(true); return; }
    return runDraft();
  };
  const runDraft = async () => {
    setConfirmOpen(false);
    setDrafting(true);
    try {
      const { data } = await API.post(`/inbox/${item.id}/draft-reply`, { instructions: steer });
      onInsert(data.draft);
      toast.success("Draft ready — read it over and edit before sending");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDrafting(false); }
  };
  const insert = (t) => { onInsert(t.rendered ?? t.body); setPickerOpen(false); toast.success(`Inserted "${t.name}"`); };

  return (
    <div className="space-y-2" data-testid="composer-assist">
      <div className="flex items-center gap-2 flex-wrap">
        <Button type="button" size="sm" variant="outline" onClick={draft} disabled={drafting} className="border-violet-300 text-violet-800 hover:bg-violet-50 dark:text-violet-200 dark:hover:bg-violet-950/30" data-testid="reply-ai-draft-btn">
          <Sparkle size={14} weight="fill" className={`mr-1 ${drafting ? "animate-pulse" : ""}`} /> {drafting ? "Drafting…" : "AI draft"}
        </Button>
        <button type="button" onClick={() => setShowSteer(v => !v)} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5" data-testid="reply-ai-steer-toggle">
          {showSteer ? "Hide" : "Tell the AI what to say"} <CaretDown size={10} className={showSteer ? "rotate-180" : ""} />
        </button>
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <Button type="button" size="sm" variant="outline" data-testid="reply-template-btn"><FileText size={14} className="mr-1" /> Saved replies</Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80 p-1" data-testid="reply-template-picker">
            {!tpl ? <p className="text-xs text-muted-foreground px-2 py-2">Loading…</p> : tpl.templates.length === 0 ? <p className="text-xs text-muted-foreground px-2 py-2">No saved replies yet.</p> : (
              <div className="max-h-72 overflow-y-auto">
                {tpl.templates.map(t => (
                  <button key={t.id} type="button" onClick={() => insert(t)} className="w-full text-left rounded-md px-2 py-1.5 hover:bg-muted" data-testid={`reply-template-${t.id}`}>
                    <p className="text-xs font-medium">{t.name}</p>
                    <p className="text-[11px] text-muted-foreground line-clamp-2">{(t.rendered ?? t.body).replace(/\s+/g, " ")}</p>
                  </button>
                ))}
              </div>
            )}
            {tpl?.can_manage && (
              <button type="button" onClick={() => { setPickerOpen(false); setManageOpen(true); }} className="w-full flex items-center gap-1.5 rounded-md px-2 py-1.5 mt-1 border-t border-border text-xs text-primary hover:bg-muted" data-testid="reply-template-manage">
                <Gear size={12} /> Manage saved replies
              </button>
            )}
          </PopoverContent>
        </Popover>
      </div>
      {showSteer && (
        <Input value={steer} onChange={e => setSteer(e.target.value)} placeholder="e.g. tell them the plumber is booked for Thursday afternoon and ask for a key-safe code" className="h-8 text-xs" data-testid="reply-ai-steer" />
      )}
      <ReplyTemplatesDialog open={manageOpen} onOpenChange={setManageOpen} onChanged={() => setTpl(null)} />
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="reply-ai-replace-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-[Chivo]">Replace what you've written?</AlertDialogTitle>
            <AlertDialogDescription>The AI draft will overwrite the current reply text. You can still edit the draft before sending.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reply-ai-replace-cancel">Keep my text</AlertDialogCancel>
            <AlertDialogAction onClick={runDraft} data-testid="reply-ai-replace-confirm">Replace with AI draft</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** Admin-only CRUD for saved replies. */
export function ReplyTemplatesDialog({ open, onOpenChange, onChanged }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // {id?, name, body}
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const { data } = await API.get("/inbox/templates"); setData(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { if (open) { setEditing(null); load(); } }, [open]);

  const save = async () => {
    setBusy(true);
    try {
      if (editing.id) await API.put(`/inbox/templates/${editing.id}`, { name: editing.name, body: editing.body });
      else await API.post("/inbox/templates", { name: editing.name, body: editing.body });
      toast.success(editing.id ? "Saved reply updated" : "Saved reply added");
      setEditing(null); await load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try { await API.delete(`/inbox/templates/${t.id}`); toast.success("Deleted"); await load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reply-templates-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">Saved replies</DialogTitle>
          <DialogDescription>Short answers staff can drop into a reply. Placeholders fill in automatically for the email being answered.</DialogDescription>
        </DialogHeader>
        {!data ? <p className="text-sm text-muted-foreground">Loading…</p> : editing ? (
          <div className="space-y-3" data-testid="reply-template-form">
            <div><Label className="text-xs">Name</Label><Input value={editing.name} onChange={e => setEditing(p => ({ ...p, name: e.target.value }))} className="mt-1" placeholder="e.g. Visit booked" data-testid="reply-template-name" /></div>
            <div><Label className="text-xs">Reply text</Label><Textarea value={editing.body} onChange={e => setEditing(p => ({ ...p, body: e.target.value }))} rows={8} className="mt-1 text-sm" data-testid="reply-template-body" /></div>
            <div className="flex flex-wrap gap-1">
              {(data.placeholders || []).map(p => (
                <button key={p.token} type="button" onClick={() => setEditing(prev => ({ ...prev, body: `${prev.body}${prev.body && !prev.body.endsWith(" ") ? " " : ""}${p.token}` }))}
                  className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-border hover:bg-muted" title={p.label} data-testid={`reply-placeholder-${p.token.replace(/[{}]/g, "")}`}>{p.token}</button>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
              <Button size="sm" onClick={save} disabled={busy || !editing.name.trim() || !editing.body.trim()} data-testid="reply-template-save">{busy ? "Saving…" : editing.id ? "Save changes" : "Add saved reply"}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {data.templates.length === 0 && <p className="text-sm text-muted-foreground">No saved replies yet.</p>}
            {data.templates.map(t => (
              <div key={t.id} className="rounded-md border border-border px-3 py-2 flex items-start justify-between gap-3" data-testid={`reply-template-row-${t.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium">{t.name}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2 whitespace-pre-line">{t.body}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" onClick={() => setEditing({ id: t.id, name: t.name, body: t.body })} className="p-1.5 rounded-md hover:bg-muted" title="Edit" data-testid={`reply-template-edit-${t.id}`}><PencilSimple size={14} /></button>
                  <button type="button" onClick={() => remove(t)} className="p-1.5 rounded-md hover:bg-muted text-red-600" title="Delete" data-testid={`reply-template-delete-${t.id}`}><Trash size={14} /></button>
                </div>
              </div>
            ))}
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setEditing({ name: "", body: "Hi {sender_name},\n\n\n\nKind regards," })} data-testid="reply-template-add"><Plus size={14} className="mr-1" /> New saved reply</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
